import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Tooltip } from 'react-tooltip';
import Icon from 'components/Icon';
import { Colors } from 'modules/theme';
import { humanizeAlertTrigger } from './alertLabels';

const Wrapper = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  line-height: 1;
`;

const Number = styled.span`
  color: ${({ alert }) => (alert ? Colors.red : Colors.black2)};
  font-weight: ${({ alert }) => (alert ? 600 : 500)};
  font-size: ${({ size }) => size}px;
  letter-spacing: -0.5px;
  font-variant-numeric: tabular-nums;
`;

const IconAnchor = styled.a`
  display: inline-flex;
  align-items: center;
  cursor: help;
  line-height: 1;
`;

const Panel = styled.div`
  padding: 2px 2px 0;
  min-width: 180px;
`;

const Heading = styled.div`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: ${Colors.gray2};
  margin-bottom: 8px;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 14px;
  padding: 3px 0;
  font-size: 12px;
  line-height: 1.3;

  & + & {
    border-top: 1px solid rgba(255, 255, 255, 0.08);
  }
`;

const RowLabel = styled.span`
  color: ${Colors.white};
  font-weight: 500;
`;

const RowCount = styled.span`
  color: ${Colors.white};
  font-weight: 700;
  font-variant-numeric: tabular-nums;
`;

let counter = 0;
const nextId = () => {
  counter += 1;
  return `alert-breakdown-${counter}`;
};

export default class AlertBreakdownTooltip extends React.PureComponent {
  constructor(props) {
    super(props);
    this.id = nextId();
  }

  renderTooltipBody = () => {
    const { breakdown } = this.props;
    return (
      <Panel>
        <Heading>Locations in alert</Heading>
        {(breakdown || []).map(b => (
          <Row key={b.type}>
            <RowLabel>{humanizeAlertTrigger(b.type)}</RowLabel>
            <RowCount>{b.count.toLocaleString()}</RowCount>
          </Row>
        ))}
      </Panel>
    );
  };

  render() {
    const { total, size, iconSize, breakdown } = this.props;
    const alert = total > 0;
    const hasBreakdown = alert && (breakdown || []).length > 0;
    return (
      <Wrapper>
        <Number alert={alert} size={size}>
          {total || 0}
        </Number>
        {hasBreakdown && (
          <>
            <IconAnchor data-tooltip-id={this.id}>
              <Icon name="info-circle" color={Colors.gray8} fontSize={iconSize} />
            </IconAnchor>
            <Tooltip
              id={this.id}
              place="top"
              opacity={1}
              style={{
                background: Colors.black2,
                color: Colors.white,
                borderRadius: 8,
                padding: '10px 14px',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
                zIndex: 9999,
                maxWidth: 260,
              }}
            >
              {this.renderTooltipBody()}
            </Tooltip>
          </>
        )}
      </Wrapper>
    );
  }
}

AlertBreakdownTooltip.propTypes = {
  breakdown: PropTypes.arrayOf(
    PropTypes.shape({
      count: PropTypes.number.isRequired,
      type: PropTypes.string.isRequired,
    }),
  ),
  iconSize: PropTypes.number,
  size: PropTypes.number,
  total: PropTypes.number,
};

AlertBreakdownTooltip.defaultProps = {
  breakdown: [],
  iconSize: 13,
  size: 14,
  total: 0,
};
